/**
 * Build-time configuration for the SPA.
 *
 * Vite inlines `import.meta.env.VITE_*` into the bundle, so everything here is
 * fixed when the image is built — see `infra/web/Dockerfile`, which declares
 * the matching `ARG`s so Railway's service variables reach the build.
 *
 * ── There is deliberately no game host here (LOCKED, architecture §16 A2/A8) ──
 *
 * The client never knows, guesses or configures the Colyseus host. It reserves
 * a seat through the api and connects to the `publicAddress` carried *inside*
 * the reservation, which names the exact process that owns the room. A
 * `VITE_GAME_URL` would be a bug waiting for the day a reservation is served by
 * process 2568.
 */

/** Base origin of the `api` service, e.g. `https://api-staging.up.railway.app`. */
export const API_URL: string = normaliseBase(import.meta.env.VITE_API_URL ?? "");

/**
 * Short build SHA of **this** service, for the splash footer.
 *
 * Labelled `web` wherever it renders (design spec §5, closing S1 QA finding
 * N7): web, api and game legitimately report different SHAs — a correct
 * consequence of Railway's watch paths — and an unlabelled number costs triage
 * time. The api's SHA is fetched at runtime from `/api/v1/version`.
 */
export const COMMIT: string = import.meta.env.VITE_COMMIT_SHA?.slice(0, 7) || "local";

/**
 * The deployed environment's name — `staging`, `production`, or `local`.
 *
 * ── S1 QA finding N1, closed ────────────────────────────────────────────────
 *
 * The Sprint 1 footer rendered Vite's `MODE`, which is `production` for any
 * optimised build. The staging splash therefore read "production" on the exact
 * screen whose job is to tell you where you are.
 *
 * `VITE_ENV` is fed from `RAILWAY_ENVIRONMENT_NAME` as a Docker build arg, and
 * `infra/web/Dockerfile` **fails the build** if it is empty — a missing value
 * must never degrade quietly into a plausible-looking lie. `local` here is for
 * `pnpm dev`, where no image was built and the truth is "this is your laptop".
 *
 * LOCKED (§5): never render `import.meta.env.MODE` in the footer.
 */
export const ENV: string = (import.meta.env.VITE_ENV ?? "").trim() || "local";

/** Production hides the environment line entirely (design spec §5). */
export const IS_PRODUCTION_ENV: boolean = ENV === "production";

/**
 * The Umami website id this build reports against — **empty means analytics is
 * off** (architecture §16g A79, T-263; §16m A100, T-291). Both environments set
 * it; unsetting it is the documented rollback to the provably-inert state.
 *
 * There is deliberately no analytics *origin* here. The tracker is served from
 * this app's own origin at `/u/script.js` and beacons to `/u/api/send`, both
 * reverse-proxied by `infra/web/Caddyfile` onto that environment's own Umami
 * service. The upstream is derived at image-build time from
 * `ANALYTICS_UPSTREAM_ORIGIN`, whose value is a Railway service reference that
 * Railway resolves per environment — so nothing about the analytics origin is
 * hand-maintained per environment, and `script-src 'self'` / `connect-src`
 * are **literally unchanged** by this feature.
 *
 * The id itself is per-environment config, like `VITE_API_URL`, and it is
 * reproducible rather than invented: it is `uuidv5(DNS-namespace, hostname)`
 * of the environment's public hostname, which is the id the website row was
 * created with in Umami. See `infra/README` §9.
 *
 * `infra/web/Dockerfile` **fails the build** if exactly one of this and
 * `ANALYTICS_UPSTREAM_ORIGIN` is set: a half-wired tracker is either a silent
 * no-op or a page requesting a script that cannot exist. It also fails if the
 * upstream names another environment's host (A100 E4).
 */
export const UMAMI_WEBSITE_ID: string = (import.meta.env.VITE_UMAMI_WEBSITE_ID ?? "").trim();

/** Trailing slashes make `${API_URL}/healthz` produce a double slash. */
function normaliseBase(raw: string): string {
  return raw.trim().replace(/\/+$/, "");
}

/**
 * A build with no api origin cannot do anything useful, and failing at the
 * first fetch with "Failed to fetch" hides why. Screens call this and render
 * the i18n `error.missingApiUrl` string instead.
 */
export function hasApiUrl(): boolean {
  return API_URL.length > 0;
}
